import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { IInventoryMovement, IInventoryMovementInput, inventoryMovementsAtom, inventoryMovementsLoadingAtom } from "./types";

type UseInventoryMovements = [IInventoryMovement[], {
    list: (query?: Record<string, any>) => Promise<IInventoryMovement[] | null>;
    getById: (id: number) => Promise<IInventoryMovement | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<IInventoryMovement[] | null>;
    create: (input: IInventoryMovementInput) => Promise<IInventoryMovement | null>;
    update: (id: number, input: IInventoryMovementInput) => Promise<IInventoryMovement | null>;
    remove: (id: number) => Promise<IInventoryMovement | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useInventoryMovements = (): UseInventoryMovements => {
    const [items, setItems] = useAtom(inventoryMovementsAtom);
    const setLoading = useSetAtom(inventoryMovementsLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getInventoryMovements(query: $query){ id inventory_item_id from_zone_id to_zone_id delta_units reason actor created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getInventoryMovements);
            return data.getInventoryMovements as IInventoryMovement[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch inventory movements.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getInventoryMovementById(id:$id){ id inventory_item_id from_zone_id to_zone_id delta_units reason actor created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getInventoryMovementById as IInventoryMovement;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedInventoryMovements(first:$first,offset:$offset,query:$query){ id inventory_item_id from_zone_id to_zone_id delta_units reason actor created_at updated_at } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedInventoryMovements as IInventoryMovement[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: IInventoryMovementInput) => {
        try { const q = `mutation($input:InventoryMovementInput!){ createInventoryMovement(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createInventoryMovement as IInventoryMovement;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: IInventoryMovementInput) => {
        try { const q = `mutation($id:ID!,$input:InventoryMovementInput!){ updateInventoryMovement(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateInventoryMovement as IInventoryMovement;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteInventoryMovement(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteInventoryMovement as IInventoryMovement;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


